/**
 * Workspace — Agents, Tools, Skills, Prompts, Connections
 *
 * The command center for AI capabilities:
 * - Agents: Card grid of built-in + custom agents with status, execution stats
 * - Tools: Catalog with test runner, Valves/UserValves pattern
 * - Skills: Markdown-based skill definitions
 * - Prompts: Template library with variable system
 * - Connections: MCP server connections with health monitoring
 */

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Bot, Wrench, Sparkles, MessageSquare, Plug, Plus, Search,
  MoreHorizontal, Play, Pause, Settings2, Activity, Shield,
  Check, AlertTriangle, Clock, ChevronRight, ExternalLink,
  Pencil, Trash2, Copy, Eye, Code, FileText, Zap,
  ToggleLeft, Server, Wifi, WifiOff, RefreshCw,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { StatusDot } from '@/components/shared/status-dot';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/workspace')({
  component: WorkspacePage,
});

// ── Mock Data ───────────────────────────────────────────────────────────────

const AGENTS = [
  { id: 'ask-argus', name: 'Ask Argus', description: 'General-purpose assistant with UX shell, clarification, and response formatting', icon: '🤖', status: 'active' as const, isBuiltin: true, executions: 1247, successRate: 0.96, avgLatency: 2.8, tools: 12 },
  { id: 'maintenance', name: 'Maintenance Intelligence', description: 'Equipment health monitoring, failure prediction, PM scheduling', icon: '🔧', status: 'active' as const, isBuiltin: true, executions: 456, successRate: 0.93, avgLatency: 3.2, tools: 8 },
  { id: 'troubleshoot', name: 'Troubleshooting', description: 'Guided playbooks for equipment diagnosis, root cause analysis', icon: '🔍', status: 'active' as const, isBuiltin: true, executions: 289, successRate: 0.91, avgLatency: 4.1, tools: 6 },
  { id: 'docs', name: 'Documentation & SOP', description: 'Document Q&A with precise citations from SOPs, manuals, P&IDs', icon: '📄', status: 'active' as const, isBuiltin: true, executions: 832, successRate: 0.97, avgLatency: 2.1, tools: 4 },
  { id: 'compliance', name: 'Compliance Monitor', description: 'ITAR/CMMC/HIPAA status checking, certification tracking', icon: '🛡️', status: 'active' as const, isBuiltin: true, executions: 156, successRate: 0.99, avgLatency: 1.8, tools: 5 },
  { id: 'safety', name: 'Safety & LOTO', description: 'Lock-Out/Tag-Out procedures, safety permits, hazard alerts', icon: '⚠️', status: 'active' as const, isBuiltin: true, executions: 98, successRate: 0.98, avgLatency: 2.5, tools: 4 },
  { id: 'work-order', name: 'Work Order Planner', description: 'WO creation, scheduling optimization, resource allocation', icon: '📋', status: 'active' as const, isBuiltin: true, executions: 367, successRate: 0.94, avgLatency: 3.6, tools: 7 },
  { id: 'parts', name: 'Parts & Inventory', description: 'Parts lookup, inventory levels, alternative parts, procurement', icon: '📦', status: 'active' as const, isBuiltin: true, executions: 201, successRate: 0.95, avgLatency: 1.9, tools: 5 },
  { id: 'reliability', name: 'Reliability', description: 'MTBF/MTTR analysis, failure mode analysis, RCM', icon: '📊', status: 'active' as const, isBuiltin: true, executions: 78, successRate: 0.92, avgLatency: 5.2, tools: 6 },
  { id: 'shift-handover', name: 'Shift Handover', description: 'Automated shift reports: alarms, work completed, open issues', icon: '📝', status: 'active' as const, isBuiltin: true, executions: 420, successRate: 0.97, avgLatency: 4.8, tools: 8 },
  { id: 'custom-shift', name: 'Shift Report Generator', description: 'Custom: Tailored shift reports for Unit 3 boiler ops', icon: '✨', status: 'draft' as const, isBuiltin: false, executions: 12, successRate: 0.83, avgLatency: 6.1, tools: 4 },
  { id: 'custom-vibration', name: 'Vibration Alert Agent', description: 'Custom: Monitors vibration thresholds, creates tickets', icon: '📡', status: 'shadow' as const, isBuiltin: false, executions: 34, successRate: 0.88, avgLatency: 3.4, tools: 3 },
];

const TOOLS = [
  { name: 'get_current_time', description: 'Returns current date and time', source: 'builtin', category: 'time', policy: 'auto_approve', active: true, calls: 2340, avgMs: 5 },
  { name: 'calculate', description: 'Mathematical calculations and unit conversions', source: 'builtin', category: 'math', policy: 'auto_approve', active: true, calls: 1560, avgMs: 8 },
  { name: 'search_knowledge', description: 'Hybrid search across document knowledge base', source: 'builtin', category: 'knowledge', policy: 'auto_approve', active: true, calls: 4521, avgMs: 245 },
  { name: 'add_memory', description: 'Store important facts for later recall', source: 'builtin', category: 'memory', policy: 'auto_approve', active: true, calls: 890, avgMs: 120 },
  { name: 'search_memories', description: 'Search stored memories by semantic similarity', source: 'builtin', category: 'memory', policy: 'auto_approve', active: true, calls: 1234, avgMs: 85 },
  { name: 'web_search', description: 'Search the web via DuckDuckGo/SearXNG', source: 'builtin', category: 'general', policy: 'auto_approve', active: false, calls: 0, avgMs: 0 },
  { name: 'fetch_url', description: 'Fetch content from a URL (SSRF protected)', source: 'builtin', category: 'general', policy: 'auto_approve', active: false, calls: 0, avgMs: 0 },
  { name: 'iq.asset.search', description: 'Search assets by tag, name, or type', source: 'mcp-tier1', category: 'iq', policy: 'auto_approve', active: true, calls: 3456, avgMs: 120 },
  { name: 'iq.asset.getDetails', description: 'Get full asset details by ID or tag', source: 'mcp-tier1', category: 'iq', policy: 'auto_approve', active: true, calls: 2890, avgMs: 95 },
  { name: 'iq.telemetry.getTimeSeries', description: 'Retrieve time series data for an asset', source: 'mcp-tier1', category: 'iq', policy: 'auto_approve', active: true, calls: 1678, avgMs: 310 },
  { name: 'iq.cmms.createWorkOrder', description: 'Create a new work order in CMMS', source: 'mcp-tier1', category: 'iq', policy: 'require_approval', active: true, calls: 367, avgMs: 890 },
  { name: 'iq.cmms.updatePMSchedule', description: 'Modify preventive maintenance schedule', source: 'mcp-tier1', category: 'iq', policy: 'require_approval', active: true, calls: 145, avgMs: 450 },
  { name: 'iq.parts.search', description: 'Search parts inventory by number or description', source: 'mcp-tier1', category: 'iq', policy: 'auto_approve', active: true, calls: 567, avgMs: 180 },
  { name: 'ext.sap.createPurchaseOrder', description: 'Create purchase order in SAP', source: 'mcp-tier2', category: 'ext', policy: 'require_approval', active: true, calls: 89, avgMs: 1200 },
  { name: 'ext.servicenow.createIncident', description: 'Create incident in ServiceNow', source: 'mcp-tier2', category: 'ext', policy: 'require_approval', active: false, calls: 0, avgMs: 0 },
];

const SKILLS = [
  { id: 'maintenance-analysis', name: 'Maintenance Analysis', description: 'Analyze equipment health data, detect anomalies, predict failures', agents: ['maintenance', 'reliability'], tools: 4 },
  { id: 'safety-lookup', name: 'Safety Procedure Lookup', description: 'Find and present safety procedures with signal word preservation', agents: ['safety'], tools: 2 },
  { id: 'document-qa', name: 'Document Q&A', description: 'Answer questions from documents with precise citations', agents: ['docs', 'ask-argus'], tools: 3 },
  { id: 'parts-search', name: 'Parts Search', description: 'Find parts, check inventory, suggest alternatives', agents: ['parts', 'work-order'], tools: 3 },
  { id: 'compliance-check', name: 'Compliance Check', description: 'Verify compliance status against framework requirements', agents: ['compliance'], tools: 5 },
  { id: 'shift-report', name: 'Shift Report Generation', description: 'Collect data and generate structured shift handover reports', agents: ['shift-handover'], tools: 6 },
  { id: 'work-planning', name: 'Work Order Planning', description: 'Plan work orders with resource allocation and scheduling', agents: ['work-order'], tools: 4 },
];

const PROMPTS = [
  { id: 'summary', name: 'Document Summary', description: 'Summarize a document into key points', variables: ['document_name', 'detail_level'], usageCount: 234 },
  { id: 'comparison', name: 'Document Comparison', description: 'Compare two versions or documents', variables: ['doc_a', 'doc_b', 'focus_areas'], usageCount: 89 },
  { id: 'safety-review', name: 'Safety Review', description: 'Review a procedure for safety compliance', variables: ['procedure_name', 'framework'], usageCount: 156 },
  { id: 'report-template', name: 'Shift Report Template', description: 'Generate structured shift handover report', variables: ['area', 'shift', 'date'], usageCount: 420 },
  { id: 'troubleshoot', name: 'Troubleshooting Guide', description: 'Step-by-step troubleshooting for equipment issues', variables: ['equipment', 'symptoms'], usageCount: 178 },
];

const CONNECTIONS = [
  { id: 'iq-native', name: 'ArgusIQ Native', type: 'tier1', status: 'online', tools: 48, latency: 95, uptime: 99.9 },
  { id: 'sap', name: 'SAP S/4HANA', type: 'tier2', status: 'online', tools: 6, latency: 1200, uptime: 98.5 },
  { id: 'servicenow', name: 'ServiceNow ITSM', type: 'tier2', status: 'offline', tools: 4, latency: 0, uptime: 0 },
  { id: 'osisoft', name: 'OSIsoft PI', type: 'tier2', status: 'online', tools: 3, latency: 340, uptime: 99.2 },
  { id: 'forge-module', name: 'Forge: Custom API', type: 'tier3', status: 'testing', tools: 2, latency: 450, uptime: 95.0 },
];

// ── Component ───────────────────────────────────────────────────────────────

type WorkspaceTab = 'agents' | 'tools' | 'skills' | 'prompts' | 'connections';

function WorkspacePage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<WorkspaceTab>('agents');
  const [search, setSearch] = useState('');

  return (
    <div className="flex flex-col h-full">
      {/* Tab bar */}
      <div className="border-b border-border/40 bg-background shrink-0">
        <div className="flex items-center justify-between px-6">
          <div className="flex items-center gap-6">
            <h1 className="text-lg font-semibold py-3 shrink-0">Workspace</h1>
            <div className="flex items-center gap-0.5">
              {([
                { key: 'agents' as const, label: 'Agents', icon: Bot, count: AGENTS.length },
                { key: 'tools' as const, label: 'Tools', icon: Wrench, count: TOOLS.length },
                { key: 'skills' as const, label: 'Skills', icon: Sparkles, count: SKILLS.length },
                { key: 'prompts' as const, label: 'Prompts', icon: MessageSquare, count: PROMPTS.length },
                { key: 'connections' as const, label: 'Connections', icon: Plug, count: CONNECTIONS.length },
              ]).map(tab => (
                <button
                  key={tab.key}
                  onClick={() => { setActiveTab(tab.key); setSearch(''); }}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors",
                    activeTab === tab.key
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
                  )}
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                  <Badge variant="secondary" className="text-[9px] h-4 min-w-[16px] px-1 ml-0.5">
                    {tab.count}
                  </Badge>
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder={`Search ${activeTab}...`}
                value={search}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
                className="h-8 w-52 pl-8 text-sm"
              />
            </div>
            <Button
              size="sm"
              onClick={() => {
                if (activeTab === 'tools') navigate({ to: '/studio/tools/new', search: { type: 'http' } });
                else if (activeTab === 'connections') navigate({ to: '/connections' });
              }}
            >
              <Plus className="h-4 w-4 mr-1" />
              {activeTab === 'agents' ? 'Create Agent' :
               activeTab === 'tools' ? 'Add Tool' :
               activeTab === 'skills' ? 'New Skill' :
               activeTab === 'prompts' ? 'New Template' :
               'Add Connection'}
            </Button>
          </div>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-6">
          {activeTab === 'agents' && <AgentsTab search={search} />}
          {activeTab === 'tools' && <ToolsTab search={search} />}
          {activeTab === 'skills' && <SkillsTab search={search} />}
          {activeTab === 'prompts' && <PromptsTab search={search} />}
          {activeTab === 'connections' && <ConnectionsTab search={search} />}
        </div>
      </ScrollArea>
    </div>
  );
}

// ── Agents Tab ──────────────────────────────────────────────────────────────

function AgentsTab({ search }: { search: string }) {
  const filtered = AGENTS.filter(a =>
    !search || a.name.toLowerCase().includes(search.toLowerCase()) || a.description.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {filtered.map(agent => (
        <Card key={agent.id} className="hover:border-primary/30 transition-colors cursor-pointer group">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center text-xl">
                  {agent.icon}
                </div>
                <div>
                  <CardTitle className="text-sm">{agent.name}</CardTitle>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <AgentStatusBadge status={agent.status} />
                    {agent.isBuiltin && (
                      <Badge variant="outline" className="text-[9px] h-4 px-1">System</Badge>
                    )}
                  </div>
                </div>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Agent actions" title="More actions">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem><Eye className="h-3.5 w-3.5 mr-2" /> View Details</DropdownMenuItem>
                  <DropdownMenuItem><Settings2 className="h-3.5 w-3.5 mr-2" /> Configure</DropdownMenuItem>
                  <DropdownMenuItem><Activity className="h-3.5 w-3.5 mr-2" /> Executions</DropdownMenuItem>
                  <DropdownMenuItem><Play className="h-3.5 w-3.5 mr-2" /> Test</DropdownMenuItem>
                  {!agent.isBuiltin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-destructive"><Trash2 className="h-3.5 w-3.5 mr-2" /> Delete</DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <CardDescription className="text-xs mt-2">{agent.description}</CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <Separator className="mb-3" />
            <div className="grid grid-cols-4 gap-2 text-center">
              <div>
                <p className="text-sm font-semibold">{agent.executions.toLocaleString()}</p>
                <p className="text-[10px] text-muted-foreground">Executions</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-green-600">{(agent.successRate * 100).toFixed(0)}%</p>
                <p className="text-[10px] text-muted-foreground">Success</p>
              </div>
              <div>
                <p className="text-sm font-semibold">{agent.avgLatency}s</p>
                <p className="text-[10px] text-muted-foreground">Avg Latency</p>
              </div>
              <div>
                <p className="text-sm font-semibold">{agent.tools}</p>
                <p className="text-[10px] text-muted-foreground">Tools</p>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function AgentStatusBadge({ status }: { status: string }) {
  const config = {
    active: { label: 'Active', className: 'bg-green-500/10 text-green-600 dark:text-green-400' },
    draft: { label: 'Draft', className: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400' },
    shadow: { label: 'Shadow', className: 'bg-purple-500/10 text-purple-600 dark:text-purple-400' },
    disabled: { label: 'Disabled', className: 'bg-gray-500/10 text-gray-500' },
  }[status] ?? { label: status, className: '' };

  return <Badge variant="outline" className={cn('text-[9px] h-4 px-1', config.className)}>{config.label}</Badge>;
}

// ── Tools Tab ───────────────────────────────────────────────────────────────

function ToolsTab({ search }: { search: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <Wrench className="h-10 w-10 text-muted-foreground/30 mb-3" />
      <h3 className="text-base font-semibold">Tools has moved to Studio</h3>
      <p className="text-sm text-muted-foreground mt-1 mb-4 max-w-md">
        The complete tool catalog with policies, valves configuration, test runner,
        and custom tool creation is now in the Studio section.
      </p>
      <Link to="/studio">
        <Button className="gap-1.5">
          <ExternalLink className="h-3.5 w-3.5" /> Go to Studio
        </Button>
      </Link>
    </div>
  );
}

// ── Skills Tab ──────────────────────────────────────────────────────────────

function SkillsTab({ search }: { search: string }) {
  const filtered = SKILLS.filter(s =>
    !search || s.name.toLowerCase().includes(search.toLowerCase()) || s.description.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {filtered.map(skill => (
        <Card key={skill.id} className="hover:border-primary/30 transition-colors cursor-pointer">
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between">
              <CardTitle className="text-sm">{skill.name}</CardTitle>
              <Button variant="ghost" size="icon" className="h-7 w-7" aria-label={`Edit skill '${skill.name}'`} title="Edit">
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            </div>
            <CardDescription className="text-xs">{skill.description}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Bot className="h-3 w-3" />
                {skill.agents.length} agent{skill.agents.length > 1 ? 's' : ''}
              </div>
              <div className="flex items-center gap-1">
                <Wrench className="h-3 w-3" />
                {skill.tools} tools
              </div>
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              {skill.agents.map(a => (
                <Badge key={a} variant="outline" className="text-[9px]">{a}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ── Prompts Tab ─────────────────────────────────────────────────────────────

function PromptsTab({ search }: { search: string }) {
  const filtered = PROMPTS.filter(p =>
    !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.description.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {filtered.map(prompt => (
        <Card key={prompt.id} className="hover:border-primary/30 transition-colors cursor-pointer group">
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <MessageSquare className="h-4 w-4 text-primary" />
                </div>
                <CardTitle className="text-sm">{prompt.name}</CardTitle>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100" aria-label={`Prompt actions for '${prompt.name}'`} title="More actions">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem><Pencil className="h-3.5 w-3.5 mr-2" /> Edit</DropdownMenuItem>
                  <DropdownMenuItem><Copy className="h-3.5 w-3.5 mr-2" /> Duplicate</DropdownMenuItem>
                  <DropdownMenuItem><Play className="h-3.5 w-3.5 mr-2" /> Use in Chat</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-destructive"><Trash2 className="h-3.5 w-3.5 mr-2" /> Delete</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <CardDescription className="text-xs mt-2">{prompt.description}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex flex-wrap gap-1">
                {prompt.variables.map(v => (
                  <Badge key={v} variant="secondary" className="text-[9px] font-mono">
                    {'{{' + v + '}}'}
                  </Badge>
                ))}
              </div>
              <span className="text-[10px] text-muted-foreground">{prompt.usageCount} uses</span>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ── Connections Tab (redirects to /connections) ─────────────────────────────

function ConnectionsTab({ search }: { search: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <Plug className="h-10 w-10 text-muted-foreground/30 mb-3" />
      <h3 className="text-base font-semibold">Connections has moved</h3>
      <p className="text-sm text-muted-foreground mt-1 mb-4 max-w-md">
        MCP servers and API connections are now managed in their own section
        with full health monitoring, circuit breaker controls, and endpoint management.
      </p>
      <Link to="/connections">
        <Button className="gap-1.5">
          <ExternalLink className="h-3.5 w-3.5" /> Go to Connections
        </Button>
      </Link>
    </div>
  );
}
